import React, { useState, useEffect } from "react";
import axios from "axios";
import { Lock, BarChart2, Bell, Download, Trash2, Phone, CheckCircle2, AlertTriangle } from "lucide-react";
import { CommissionRatesBlock, CustomerTransparencyNote } from "../../components/commission/CommissionInfo";
import { ContactDetailsBlock } from "../../components/profile/ContactDetailsBlock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ConsentManagement = ({ user, showToast }) => {
  const [consents, setConsents] = useState(null);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    fetchConsents();
  }, []);

  const fetchConsents = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/user/consents`);
      setConsents(response.data);
    } catch (error) {
      console.error("Error fetching consents:", error);
      showToast("Ошибка загрузки согласий", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/user/consent-history`);
      setHistory(response.data.history);
      setShowHistory(true);
    } catch (error) {
      console.error("Error fetching consent history:", error);
      showToast("Ошибка загрузки истории", "error");
    } finally {
      setLoading(false);
    }
  };

  const updateConsent = async (field, value) => {
    try {
      setLoading(true);
      await axios.put(`${API}/user/consents`, {
        [field]: value
      });
      showToast("Согласие обновлено", "success");
      fetchConsents(); // Обновляем данные
    } catch (error) {
      console.error("Error updating consent:", error);
      showToast("Ошибка обновления согласия", "error");
    } finally {
      setLoading(false);
    }
  };

  if (!consents) {
    return (
      <div className="reversia-card p-6">
        <div className="text-center py-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 mx-auto" style={{borderColor:'var(--rv-teal)'}}></div>
          <p className="mt-2 text-sm" style={{color:'var(--rv-text-muted)'}}>Загрузка согласий...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="reversia-card p-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-base font-semibold flex items-center gap-2" style={{fontFamily:'Manrope,sans-serif', color:'var(--rv-text-primary)'}}>
          <CheckCircle2 size={16} style={{color:'var(--rv-teal)'}}/>Управление согласиями (152-ФЗ)
        </h3>
        <button
          onClick={fetchHistory}
          disabled={loading}
          className="text-sm underline"
          style={{color:'var(--rv-teal)', background:'none', border:'none', cursor:'pointer'}}
        >
          История изменений
        </button>
      </div>

      <div className="space-y-4">
        <div className="space-y-3">
          <h4 className="font-medium text-sm" style={{color:'var(--rv-text-primary)'}}>Обязательные согласия:</h4>
          
          <div className="p-4 rounded-lg" style={{background:'rgba(16,185,129,0.04)', border:'1px solid rgba(16,185,129,0.15)'}}>
            <div className="flex items-center justify-between">
              <div>
                <h5 className="font-medium" style={{color:'var(--rv-text-primary)'}}>Обработка персональных данных</h5>
                <p className="text-sm mt-1" style={{color:'var(--rv-text-muted)'}}>
                  Базовое согласие на обработку ваших данных для работы сервиса
                </p>
              </div>
              <div className="flex items-center space-x-2">
                <span className="font-medium flex items-center gap-1 text-sm" style={{color:'#10b981'}}><CheckCircle2 size={13}/>Активно</span>
                <span className="text-xs" style={{color:'var(--rv-text-muted)'}}>(обязательно)</span>
              </div>
            </div>
          </div>

          <div className="p-4 rounded-lg" style={{background:'rgba(16,185,129,0.04)', border:'1px solid rgba(16,185,129,0.15)'}}>
            <div className="flex items-center justify-between">
              <div>
                <h5 className="font-medium" style={{color:'var(--rv-text-primary)'}}>Условия использования</h5>
                <p className="text-sm mt-1" style={{color:'var(--rv-text-muted)'}}>
                  Согласие с правилами платформы и пользовательским соглашением
                </p>
              </div>
              <div className="flex items-center space-x-2">
                <span className="font-medium flex items-center gap-1 text-sm" style={{color:'#10b981'}}><CheckCircle2 size={13}/>Активно</span>
                <span className="text-xs" style={{color:'var(--rv-text-muted)'}}>(обязательно)</span>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <h4 className="font-medium text-sm" style={{color:'var(--rv-text-primary)'}}>Дополнительные согласия:</h4>
          
          <div className="p-4 rounded-lg" style={{background:'rgba(13,148,136,0.04)', border:'1px solid rgba(13,148,136,0.1)'}}>
            <div className="flex items-center justify-between">
              <div className="flex-grow">
                <h5 className="font-medium" style={{color:'var(--rv-text-primary)'}}>Маркетинговые рассылки</h5>
                <p className="text-sm mt-1" style={{color:'var(--rv-text-muted)'}}>
                  Получение информационных писем о новых функциях и акциях
                </p>
              </div>
              <div className="flex items-center space-x-3">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={consents.consents?.marketingEmails || false}
                    onChange={(e) => updateConsent('marketingEmails', e.target.checked)}
                    disabled={loading}
                    className="h-4 w-4 rounded"
                    style={{accentColor:'var(--rv-teal)'}}
                  />
                  <span className="ml-2 text-sm" style={{color:'var(--rv-text-primary)'}}>
                    {consents.consents?.marketingEmails ? 'Включено' : 'Отключено'}
                  </span>
                </label>
              </div>
            </div>
          </div>
        </div>

        {consents.consents?.consentDate && (
          <div className="p-3 rounded text-sm" style={{background:'rgba(13,148,136,0.04)', border:'1px solid rgba(13,148,136,0.1)'}}>
            <span style={{color:'var(--rv-text-muted)'}}>
              Последнее обновление: {new Date(consents.consents.consentDate).toLocaleString('ru-RU')}
            </span>
          </div>
        )}

        <div className="p-4 rounded-lg" style={{background:'rgba(217,119,6,0.05)', border:'1px solid rgba(217,119,6,0.15)'}}>
          <h5 className="font-medium mb-2 text-sm flex items-center gap-1.5" style={{color:'#92400e'}}>
            <AlertTriangle size={13}/>Важная информация
          </h5>
          <ul className="text-sm space-y-1" style={{color:'#b45309'}}>
            <li>• Обязательные согласия нельзя отозвать без удаления аккаунта</li>
            <li>• Дополнительные согласия можно изменить в любое время</li>
            <li>• Все изменения согласий логируются согласно 152-ФЗ</li>
            <li>• При отзыве согласий некоторые функции могут быть недоступны</li>
          </ul>
        </div>
      </div>

      {showHistory && (
        <div className="mt-6 pt-4" style={{borderTop:'1px solid var(--rv-border)'}}>
          <div className="flex justify-between items-center mb-3">
            <h4 className="font-medium" style={{color:'var(--rv-text-primary)'}}>История изменений согласий</h4>
            <button
              onClick={() => setShowHistory(false)}
              className="text-sm"
              style={{color:'var(--rv-text-muted)', background:'none', border:'none', cursor:'pointer'}}
            >
              Скрыть
            </button>
          </div>
          
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {history.length > 0 ? (
              history.map((record, index) => (
                <div key={index} className="p-3 rounded text-sm" style={{background:'rgba(13,148,136,0.04)', border:'1px solid rgba(13,148,136,0.1)'}}>
                  <div className="flex justify-between items-start">
                    <div>
                      <span className="font-medium" style={{color:'var(--rv-text-primary)'}}>
                        {record.event_type === 'initial_registration_consent' ? 'Первичное согласие' : 'Обновление'}
                      </span>
                      {record.changed_fields && (
                        <span className="ml-2" style={{color:'var(--rv-text-muted)'}}>
                          ({record.changed_fields.join(', ')})
                        </span>
                      )}
                    </div>
                    <span style={{color:'var(--rv-text-muted)'}}>
                      {new Date(record.created_at).toLocaleString('ru-RU')}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-center py-4" style={{color:'var(--rv-text-muted)'}}>История изменений пуста</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Privacy Settings Component
export const PrivacySettings = ({ user, showToast }) => {
  const [loading, setLoading] = useState(false);
  const [userDataOverview, setUserDataOverview] = useState(null);

  // Загружаем обзор данных пользователя при открытии
  useEffect(() => {
    fetchUserDataOverview();
  }, []);

  const fetchUserDataOverview = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/user/data-overview`);
      setUserDataOverview(response.data);
    } catch (error) {
      console.error("Error fetching user data overview:", error);
      showToast("Ошибка загрузки данных", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDataExport = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/user/export-data`, {
        responseType: 'blob'
      });
      
      // Создаем ссылку для скачивания
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `reversia_data_${user.email}_${new Date().toISOString().split('T')[0]}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      showToast("Данные успешно экспортированы", "success");
    } catch (error) {
      console.error("Error exporting data:", error);
      showToast("Ошибка экспорта данных", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleConsentRevoke = async (consentType) => {
    if (!confirm(`Вы действительно хотите отозвать согласие на ${consentType}? Это может ограничить функциональность сервиса.`)) {
      return;
    }

    try {
      setLoading(true);
      await axios.post(`${API}/user/revoke-consent`, {
        consentType: consentType
      });
      
      showToast("Согласие успешно отозвано", "success");
      fetchUserDataOverview(); // Обновляем данные
    } catch (error) {
      console.error("Error revoking consent:", error);
      showToast("Ошибка при отзыве согласия", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleAccountDeletion = async () => {
    const confirmText = prompt(
      'ВНИМАНИЕ! Удаление аккаунта необратимо.\n\n' +
      'Будут удалены:\n' +
      '- Профиль и персональные данные\n' +
      '- История заданий (кроме связанных с налоговой отчетностью)\n' +
      '- Загруженные медиафайлы\n\n' +
      'НЕ будут удалены (требования 152-ФЗ):\n' +
      '- Фискальные чеки (срок хранения 10 лет)\n' +
      '- Данные для налоговой отчетности\n\n' +
      'Для подтверждения введите ваш email:'
    );

    if (confirmText !== user.email) {
      showToast("Email не совпадает. Удаление отменено.", "error");
      return;
    }

    try {
      setLoading(true);
      await axios.delete(`${API}/user/delete-account`);
      
      showToast("Запрос на удаление аккаунта отправлен. Вы получите подтверждение в течение 30 дней.", "info");
      
      // Выходим из системы через 3 секунды
      setTimeout(() => {
        localStorage.removeItem('reversia_session');
        window.location.reload();
      }, 3000);
    } catch (error) {
      console.error("Error requesting account deletion:", error);
      showToast("Ошибка при запросе удаления аккаунта", "error");
    } finally {
      setLoading(false);
    }
  };

  if (loading && !userDataOverview) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto" style={{borderColor:'var(--rv-teal)'}}></div>
        <p className="mt-2" style={{color:'var(--rv-text-muted)'}}>Загрузка настроек приватности...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2" style={{fontFamily:'Manrope,sans-serif', color:'var(--rv-text-primary)'}}><Lock size={20} style={{color:'var(--rv-teal)'}}/>Настройки приватности</h2>
          <p className="text-sm mt-1" style={{color:'var(--rv-text-muted)'}}>Управляйте своими персональными данными согласно 152-ФЗ</p>
        </div>
      </div>

      <ContactDetailsBlock showToast={showToast} />

      {/* Обзор данных */}
      {user.role === "executor" && <CommissionRatesBlock />}
      {user.role === "customer" && <CustomerTransparencyNote />}

      {/* Обзор данных */}
      <div className="reversia-card p-6">
        <h3 className="text-base font-semibold mb-4 flex items-center gap-2" style={{fontFamily:'Manrope,sans-serif', color:'var(--rv-text-primary)'}}><BarChart2 size={16} style={{color:'var(--rv-teal)'}}/>Обзор ваших данных</h3>
        
        {userDataOverview ? (
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <div className="p-3 rounded-lg" style={{background:'rgba(13,148,136,0.04)', border:'1px solid rgba(13,148,136,0.1)'}}>
                <h4 className="font-medium text-sm mb-2" style={{color:'var(--rv-text-primary)'}}>Основная информация</h4>
                <ul className="text-sm space-y-1" style={{color:'var(--rv-text-muted)'}}>
                  <li>Имя: {user.name}</li>
                  <li>Email: {user.email}</li>
                  <li>Роль: {user.role === 'customer' ? 'Заказчик' : 
                           user.role === 'admin' ? 'Администратор' : 'Исполнитель'}</li>
                  <li>Дата регистрации: {new Date(userDataOverview.registrationDate).toLocaleDateString('ru-RU')}</li>
                </ul>
              </div>
              
              <div className="p-3 rounded-lg" style={{background:'rgba(16,185,129,0.04)', border:'1px solid rgba(16,185,129,0.15)'}}>
                <h4 className="font-medium text-sm mb-2" style={{color:'var(--rv-text-primary)'}}>Активность</h4>
                <ul className="text-sm space-y-1" style={{color:'var(--rv-text-muted)'}}>
                  <li>Заданий выполнено: {userDataOverview.tasksCount || 0}</li>
                  <li>Загружено файлов: {userDataOverview.filesCount || 0}</li>
                  <li>Сообщений отправлено: {userDataOverview.messagesCount || 0}</li>
                </ul>
              </div>
            </div>
            
            <div className="space-y-3">
              <div className="p-3 rounded-lg" style={{background:'rgba(217,119,6,0.04)', border:'1px solid rgba(217,119,6,0.15)'}}>
                <h4 className="font-medium text-sm mb-2" style={{color:'var(--rv-text-primary)'}}>Согласия</h4>
                <ul className="text-sm space-y-1" style={{color:'var(--rv-text-muted)'}}>
                  <li className="flex items-center gap-1"><CheckCircle2 size={12} style={{color:'#10b981'}}/>Обработка персональных данных</li>
                  <li className="flex items-center gap-1">{userDataOverview.geoConsent ? <CheckCircle2 size={12} style={{color:'#10b981'}}/> : <span className="inline-block w-3 h-3 rounded-full" style={{background:'#ef4444'}}/>} Геолокационные уведомления</li>
                  <li className="flex items-center gap-1">{userDataOverview.notificationConsent ? <CheckCircle2 size={12} style={{color:'#10b981'}}/> : <span className="inline-block w-3 h-3 rounded-full" style={{background:'#ef4444'}}/>} Уведомления о заданиях</li>
                </ul>
              </div>
              
              <div className="p-3 rounded-lg" style={{background:'rgba(99,102,241,0.04)', border:'1px solid rgba(99,102,241,0.15)'}}>
                <h4 className="font-medium text-sm mb-2" style={{color:'var(--rv-text-primary)'}}>Хранение данных</h4>
                <p className="text-sm" style={{color:'var(--rv-text-muted)'}}>
                  Ваши данные хранятся на серверах в РФ согласно требованиям 152-ФЗ. 
                  Фискальные чеки сохраняются 10 лет согласно налоговому законодательству.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div style={{color:'var(--rv-text-muted)'}}>Загрузка данных...</div>
        )}
      </div>

      {/* Управление согласиями 152-ФЗ */}
      <ConsentManagement user={user} showToast={showToast} />

      {/* Управление уведомлениями */}
      <div className="reversia-card p-6">
        <h3 className="text-base font-semibold mb-4 flex items-center gap-2" style={{fontFamily:'Manrope,sans-serif', color:'var(--rv-text-primary)'}}><Bell size={16} style={{color:'var(--rv-teal)'}}/>Управление уведомлениями</h3>
        
        <div className="space-y-3">
          {[
            { title: 'Геолокационные уведомления', desc: 'Разрешение на использование вашего местоположения для уведомлений о заданиях поблизости', type: 'geolocation' },
            { title: 'Push-уведомления', desc: 'Согласие на получение уведомлений о новых заданиях и важных обновлениях', type: 'notifications' },
          ].map(({ title, desc, type }) => (
            <div key={type} className="p-4 rounded-lg" style={{border:'1px solid var(--rv-border)'}}>
              <div className="flex justify-between items-start">
                <div>
                  <h4 className="font-medium text-sm" style={{color:'var(--rv-text-primary)'}}>{title}</h4>
                  <p className="text-sm mt-0.5" style={{color:'var(--rv-text-muted)'}}>{desc}</p>
                </div>
                <button
                  onClick={() => handleConsentRevoke(type)}
                  disabled={loading}
                  className="ml-4 flex-shrink-0 px-3 py-1.5 rounded text-sm"
                  style={{background:'rgba(217,119,6,0.08)', color:'#d97706', border:'1px solid rgba(217,119,6,0.2)', cursor:'pointer'}}
                >
                  {loading ? 'Обработка...' : 'Отозвать'}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Экспорт данных */}
      <div className="reversia-card p-6">
        <h3 className="text-base font-semibold mb-4 flex items-center gap-2" style={{fontFamily:'Manrope,sans-serif', color:'var(--rv-text-primary)'}}><Download size={16} style={{color:'var(--rv-teal)'}}/>Экспорт ваших данных</h3>
        
        <div className="p-4 rounded-lg" style={{background:'rgba(13,148,136,0.04)', border:'1px solid rgba(13,148,136,0.1)'}}>
          <p className="text-sm mb-3" style={{color:'var(--rv-text-muted)'}}>
            Согласно GDPR и 152-ФЗ, вы можете получить копию всех ваших данных в машиночитаемом формате.
          </p>
          <button
            onClick={handleDataExport}
            disabled={loading}
            className="flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium"
            style={{background:'var(--rv-teal)', color:'white', border:'none', cursor:'pointer', opacity: loading ? 0.5 : 1}}
          >
            <Download size={14}/>{loading ? 'Подготовка...' : 'Скачать мои данные (JSON)'}
          </button>
        </div>
      </div>

      {/* Удаление аккаунта */}
      <div className="reversia-card p-6" style={{borderColor:'rgba(239,68,68,0.3)'}}>
        <h3 className="text-base font-semibold mb-4 flex items-center gap-2" style={{fontFamily:'Manrope,sans-serif', color:'var(--rv-error)'}}><Trash2 size={16}/>Удаление аккаунта</h3>
        
        <div className="p-4 rounded-lg" style={{background:'rgba(239,68,68,0.04)', border:'1px solid rgba(239,68,68,0.15)'}}>
          <p className="text-sm mb-3" style={{color:'#b91c1c'}}>
            <strong>Внимание!</strong> Удаление аккаунта необратимо. Некоторые данные останутся в системе 
            согласно требованиям налогового законодательства (фискальные чеки, данные НПД).
          </p>
          
          <div className="mb-4 text-sm">
            <h4 className="font-medium mb-1" style={{color:'var(--rv-text-primary)'}}>Что будет удалено:</h4>
            <ul className="list-disc list-inside space-y-0.5" style={{color:'#b91c1c'}}>
              <li>Профиль и персональные данные</li>
              <li>История заданий (кроме связанных с налоговой отчетностью)</li>
              <li>Загруженные медиафайлы</li>
              <li>Чаты и сообщения</li>
            </ul>
            
            <h4 className="font-medium mb-1 mt-3" style={{color:'var(--rv-text-primary)'}}>Что останется (согласно 152-ФЗ):</h4>
            <ul className="list-disc list-inside space-y-0.5" style={{color:'var(--rv-text-muted)'}}>
              <li>Фискальные чеки (10 лет)</li>
              <li>Данные для налоговой отчетности</li>
              <li>Обезличенная статистика</li>
            </ul>
          </div>
          
          <button
            onClick={handleAccountDeletion}
            disabled={loading}
            className="flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium"
            style={{background:'var(--rv-error)', color:'white', border:'none', cursor:'pointer', opacity: loading ? 0.5 : 1}}
          >
            <Trash2 size={14}/>{loading ? 'Обработка...' : 'Запросить удаление аккаунта'}
          </button>
        </div>
      </div>

      {/* Контакты */}
      <div className="reversia-card p-6">
        <h3 className="text-base font-semibold mb-3 flex items-center gap-2" style={{fontFamily:'Manrope,sans-serif', color:'var(--rv-text-primary)'}}><Phone size={16} style={{color:'var(--rv-teal)'}}/>Вопросы по приватности</h3>
        <p className="text-sm" style={{color:'var(--rv-text-muted)'}}>
          По всем вопросам обработки персональных данных обращайтесь на{' '}
          <a href="mailto:support@reversia.ru" style={{color:'var(--rv-teal)', textDecoration:'none'}}>
            support@reversia.ru
          </a>
        </p>
        <p className="text-xs mt-1" style={{color:'var(--rv-text-muted)'}}>
          Ответ в течение 3 рабочих дней. Для GDPR запросов укажите в теме "GDPR Request".
        </p>
      </div>
    </div>
  );
};
